
import React, { useRef, useEffect, useCallback } from 'react';
import { Search, Bell, Settings, ChevronDown, Cpu, Users, History, Loader2, Layout, ArrowLeft, AlertTriangle, UserPlus, BarChart3, CheckCheck, Info, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSearch, SearchResult } from '../hooks/useSearch';
import { apiUrl } from '../../utils/api';
import UserProfile from '../../components/UserProfile';
import { AdminView } from '../types';
import { useNavigate } from 'react-router-dom';

interface NotificationItem {
  id: number;
  title: string;
  body: string;
  category: string;
  severity: string;
  is_read: boolean;
  created_at: string;
  source: string | null;
  action_url: string | null;
}

interface TopBarProps { // Nudge to force rebuild
  onViewChange?: (view: AdminView) => void;
  selectedAgent: { id: string; name: string };
  setSelectedAgent: (agent: { id: string; name: string }) => void;
}

const TopBar: React.FC<TopBarProps> = ({ onViewChange, selectedAgent, setSelectedAgent }) => {
  const { user } = useAuth();
  const { query, setQuery, results, isLoading, isOpen, setIsOpen } = useSearch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const agentDropdownRef = useRef<HTMLDivElement>(null);

  const [agents, setAgents] = React.useState<{ id: string; name: string }[]>([]);
  const [isAgentOpen, setIsAgentOpen] = React.useState(false);
  const [hasUnread, setHasUnread] = React.useState(false);
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([]);
  const [isNotifOpen, setIsNotifOpen] = React.useState(false);
  const [notifLoading, setNotifLoading] = React.useState(false);
  const notifDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await fetch(apiUrl('/agent-builder/agents'));
        if (response.ok) {
          const data = await response.json();

          // Deduplicate agents by name on the frontend to be absolutely sure
          const uniqueAgents: { id: string; name: string }[] = [];
          const seenNorms = new Set<string>();

          data.forEach((agent: any) => {
            if (!agent.name) return;
            // Robust normalization: lowercase, strip "agent", remove non-alphanumeric
            const normalized = agent.name.toLowerCase()
              .replace(/[_ ]?agent$/i, '')
              .replace(/[^a-z0-9]/g, '')
              .trim();

            if (!seenNorms.has(normalized)) {
              seenNorms.add(normalized);
              uniqueAgents.push(agent);
            }
          });

          setAgents(uniqueAgents);
          // Defaulting to 'Agents' as initial state
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
      }
    };
    fetchAgents();
  }, []);

  // Poll for unread notifications
  useEffect(() => {
    const checkUnread = async () => {
      try {
        const res = await fetch(apiUrl('/notifications/?include_read=false&limit=1'));
        if (res.ok) {
          const data = await res.json();
          setHasUnread(Array.isArray(data) && data.length > 0);
        }
      } catch { /* ignore */ }
    };
    checkUnread();
    const interval = setInterval(checkUnread, 30_000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await fetch(apiUrl('/notifications/?include_read=true&limit=20'));
      if (res.ok) {
        const data = await res.json();
        setNotifications(Array.isArray(data) ? data : []);
        setHasUnread(data.some((n: NotificationItem) => !n.is_read));
      }
    } catch { /* ignore */ }
    setNotifLoading(false);
  }, []);

  const handleBellClick = () => {
    if (!isNotifOpen) {
      fetchNotifications();
    }
    setIsNotifOpen(!isNotifOpen);
  };

  const markOneRead = async (id: number) => {
    try {
      await fetch(apiUrl(`/notifications/${id}/read`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_read: true }),
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setHasUnread(prev => notifications.some(n => n.id !== id && !n.is_read));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await fetch(apiUrl('/notifications/mark-all-read'), { method: 'POST' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setHasUnread(false);
    } catch { /* ignore */ }
  };

  const deleteAll = async () => {
    try {
      await fetch(apiUrl('/notifications/delete-all'), { method: 'POST' });
      setNotifications([]);
      setHasUnread(false);
    } catch { /* ignore */ }
  };

  const getCategoryIcon = (category: string, severity: string) => {
    if (severity === 'critical' || severity === 'error') return <AlertTriangle size={14} className="text-red-500" />;
    switch (category) {
      case 'signup': return <UserPlus size={14} className="text-blue-500" />;
      case 'agent_failure': return <AlertTriangle size={14} className="text-amber-500" />;
      case 'weekly_digest': return <BarChart3 size={14} className="text-emerald-500" />;
      default: return <Info size={14} className="text-slate-400" />;
    }
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target as Node)) {
        setIsAgentOpen(false);
      }
      if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  const handleResultClick = (result: SearchResult) => {
    if (onViewChange) {
      onViewChange(AdminView[result.link as keyof typeof AdminView]);
    }
    setIsOpen(false);
    setQuery('');
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'Agent': return <Cpu size={14} className="text-blue-500" />;
      case 'User': return <Users size={14} className="text-indigo-500" />;
      case 'Log': return <History size={14} className="text-slate-500" />;
      case 'Page': return <Layout size={14} className="text-emerald-500" />;
      default: return <Search size={14} />;
    }
  };

  return (
    <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6 sticky top-0 z-40 transition-colors duration-300">
      <div className="flex items-center gap-4 flex-1 max-w-xl">
        <button
          onClick={() => navigate('/')}
          className="p-2 -ml-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          title="Back to Chat"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="relative w-full group" ref={dropdownRef}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${isLoading ? 'text-transparent' : 'text-slate-400 group-focus-within:text-blue-500'}`} size={18} />
            {isLoading && (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" size={18} />
            )}
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => query.trim() && setIsOpen(true)}
              placeholder="Search agents, users, or logs..."
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium text-slate-900 dark:text-slate-100"
            />
          </div>

          {/* Search Results Dropdown */}
          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl overflow-hidden z-50 animate-fadeIn min-w-[320px]">
              <div className="max-h-[400px] overflow-y-auto p-2">
                {results.length > 0 ? (
                  <div className="space-y-1">
                    {results.map((result, idx) => (
                      <button
                        key={`${result.type}-${result.id}-${idx}`}
                        onClick={() => handleResultClick(result)}
                        className="w-full text-left p-3 hover:bg-slate-50 rounded-xl transition-colors flex items-start gap-3 group"
                      >
                        <div className="mt-1 p-1.5 bg-slate-100 rounded-lg group-hover:bg-white transition-colors">
                          {getIcon(result.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-900 truncate">{result.title}</span>
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-md">{result.type}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{result.subtitle}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-sm text-slate-500 font-medium">{isLoading ? 'Searching...' : 'No results found'}</p>
                    <p className="text-xs text-slate-400 mt-1">Try a different keyword</p>
                  </div>
                )}
              </div>
              <div className="bg-slate-50 dark:bg-slate-900/50 p-2 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Workspace/Agent Selector */}
        <div className="relative" ref={agentDropdownRef}>
          <div
            onClick={() => setIsAgentOpen(!isAgentOpen)}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{selectedAgent.name}</span>
            <ChevronDown size={16} className={`text-slate-400 transition-transform ${isAgentOpen ? 'rotate-180' : ''}`} />
          </div>

          {isAgentOpen && (
            <div className="absolute top-full right-0 mt-2 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 py-1 max-h-[300px] overflow-y-auto">
              {agents.length > 0 ? (
                <>
                  <div
                    onClick={() => {
                      setSelectedAgent({ id: 'all', name: 'Agents' });
                      setIsAgentOpen(false);
                    }}
                    className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-50 mb-1 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    Agents
                  </div>
                  {agents.map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => {
                        setSelectedAgent({ id: agent.id, name: agent.name.replace(/_/g, ' ') });
                        setIsAgentOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 ${selectedAgent.id === agent.id ? 'text-blue-600 font-semibold bg-blue-50/50' : 'text-slate-700 dark:text-slate-300'}`}
                    >
                      <Cpu size={14} className={selectedAgent.id === agent.id ? 'text-blue-500' : 'text-slate-400'} />
                      <span className="truncate">{agent.name.replace(/_/g, ' ')}</span>
                    </button>
                  ))}
                </>
              ) : (
                <div className="px-4 py-3 text-center">
                  <p className="text-xs text-slate-500 italic">No agents found</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="relative" ref={notifDropdownRef}>
          <button
            onClick={handleBellClick}
            className="relative p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            {hasUnread && (
              <span className="absolute top-1.5 left-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-slate-900"></span>
            )}
            <Bell size={20} />
          </button>

          {isNotifOpen && (
            <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Notifications</h3>
                <div className="flex items-center gap-2">
                  {hasUnread && (
                    <button
                      onClick={markAllRead}
                      className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 font-medium"
                    >
                      <CheckCheck size={12} /> Mark all read
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      onClick={deleteAll}
                      className="flex items-center gap-1 text-[11px] text-red-500 hover:text-red-600 font-medium"
                    >
                      <Trash2 size={12} /> Delete all
                    </button>
                  )}
                </div>
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                {notifLoading ? (
                  <div className="py-8 text-center">
                    <Loader2 className="mx-auto text-blue-500 animate-spin" size={20} />
                    <p className="text-xs text-slate-400 mt-2">Loading...</p>
                  </div>
                ) : notifications.length > 0 ? (
                  <div className="divide-y divide-slate-50 dark:divide-slate-700">
                    {notifications.map((notif) => (
                      <div
                        key={notif.id}
                        onClick={() => !notif.is_read && markOneRead(notif.id)}
                        className={`px-4 py-3 flex gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${!notif.is_read ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}`}
                      >
                        <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                          {getCategoryIcon(notif.category, notif.severity)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm truncate ${!notif.is_read ? 'font-bold text-slate-900 dark:text-white' : 'font-medium text-slate-600 dark:text-slate-300'}`}>
                              {notif.title}
                            </p>
                            {!notif.is_read && (
                              <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{notif.body}</p>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{timeAgo(notif.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-10 text-center">
                    <Bell className="mx-auto text-slate-300 dark:text-slate-600" size={28} />
                    <p className="text-sm text-slate-500 font-medium mt-2">No notifications</p>
                    <p className="text-xs text-slate-400 mt-0.5">You're all caught up!</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>

        <UserProfile onViewChange={onViewChange} />
      </div>
    </header>
  );
};

export default TopBar;
